<template>
  <div class="flex">
    <div class="mt-1" :class="{ 'flex items-center': displayStyle == 'inline' }">
      <div v-if="displayStyle !== 'inline'" class="text-common-grey whitespace-nowrap">
        {{ label }}
      </div>
      <div class="flex mt-2 gap-x-1">
        <div v-for="(user, i) in users" :key="user?.id" class="relative has-tooltip">
          <div>
            <img
              v-if="$store.state.data.users.find((u) => u.id == user?.id)?.avatar"
              onerror="this.onerror=null; this.src=window.default_avatar;"
              class="inline-block w-10 h-10 rounded-full"
              :src="$store.state.data.users.find((u) => u.id == user?.id)?.avatar"
              alt="">
            <Avatar v-else-if="user?.id?.includes(`$`)" :hover-effect="true" :first-name="user.label" />

            <Avatar v-else :hover-effect="true" :first-name="Record.from(user).first_name" :last-name="Record.from(user).last_name" />
          </div>

          <span
            v-if="user?.id?.includes(`$`)"
            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ user.label }}
          </span>

          <span
            v-else
            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ Record.from(user).full_name }}
          </span>
          <div
            v-if="!disabled"
            class="absolute w-4 h-4 text-xs text-red-400 border rounded-full cursor-pointer bg-light-screen-background-grey dark:bg-dark-screen-background dark:border-dark-divider-border z-1 -top-2 -right-1"
            @click="handleRemoveUser(i)">
            <span class="ml-[4px] relative bottom-[2px]">x</span>
          </div>
        </div>
        <div
          v-if="!disabled && !(users.length == 1 && !multiSelect)"
          class="justify-center mr-2 text-center align-middle cursor-pointer dark:border-dark-divider-border h-9 w-9">
          <div class="relative">
            <div class="mt-1 w-8 h-8 border border-dotted rounded-full dark:border-dark-divider-border">
              <Plus class="absolute w-5 h-5 text-common-grey mt-[5px] ml-[5px]" @click="add_user_mode = !add_user_mode" />
            </div>
          </div>

          <div v-if="add_user_mode" class="w-full mt-2 min-h-[50px] min-w-[250px]">
            <UserandContactsAutoCompleteInput
              :add-new-user="false"
              :excluded-contacts="excludedContacts"
              :label="label"
              :input-labels="inputLabels"
              :is-box-background="true"
              :users="all_users"
              value-attr="full_name"
              @focusout="
                () => {
                  $utils.debounce(() => {
                    add_user_mode = false
                  }, 200)
                }
              "
              @reset="add_user_mode = false"
              @chosen="handleAddUser($event)" />
          </div>
        </div>
      </div>
      <div v-if="displayStyle == 'inline'" class="mt-1 text-sm text-common-grey whitespace-nowrap">
        {{ label }}
      </div>
    </div>
  </div>
</template>

<script>
import { Plus } from "lucide-vue-next"
import UserandContactsAutoCompleteInput from "@/components/shared/UserandContactsAutoCompleteInput.vue"

import Avatar from "@/components/shared/Avatar.vue"
import { Record } from "@/models/record"

export default {
  components: { Plus, UserandContactsAutoCompleteInput, Avatar },
  props: {
    excludedContacts: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      required: false
    },
    inputLabels: {
      type: Array,
      required: false
    },

    selectedTeamMembers: {
      type: Array,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiSelect: {
      type: Boolean,
      default: true
    },
    displayStyle: {
      type: String,
      required: false
    },
    autocompleteOptions: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ["done", "changed", "update:selectedTeamMembers"],
  data() {
    return {
      Record,
      add_user_mode: false,
      all_users: [],
      users: []
    }
  },
  watch: {
    selectedTeamMembers: {
      deep: true,
      handler() {
        this.users = this.selectedTeamMembers.map((item) =>
          item.id
            ? item
            : this.$store.state.data.users.concat(this.autocompleteOptions).find((o_u) => o_u.id == item)
              ? this.$store.state.data.users.concat(this.autocompleteOptions).find((o_u) => o_u.id == item)
              : item
        )
      }
    }
  },

  created() {
    this.all_users = this.$utils.clone(this.$store.state.data.users)
    this.autocompleteOptions.map((r) => {
      this.all_users.push({ ...r, full_name: r.label, name: r.label, first_name: r.label, id: r.value, selected_team_member: r.value })
    })
    if (this.selectedTeamMembers?.length) {
      this.users = this.selectedTeamMembers.map((item) =>
        item?.id ? item : this.$store.state.data.users.concat(this.autocompleteOptions).filter((o_u) => o_u?.id == item)[0]
      )
    }

    this.all_users = this.all_users.filter((x) => this.users.filter((s) => s?.id == x?.id).length == 0)
  },

  methods: {
    handleRemoveUser(i) {
      if (this.users[i].id.includes(`$`)) {
        const user_obj = this.users[i]
        this.all_users.push({
          ...user_obj,
          full_name: user_obj.label,
          name: user_obj.label,
          first_name: user_obj.label,
          id: user_obj.value,
          selected_team_member: user_obj.value
        })
      } else {
        this.all_users.push(this.users[i])
      }
      this.users.splice(i, 1)

      this.$emit("update:selectedTeamMembers", this.users)
    },

    handleAddUser(e) {
      this.users.push(e)
      this.all_users = this.all_users.filter((x) => this.users.filter((s) => s.id == x.id).length == 0)
      this.add_user_mode = false
      this.$emit("update:selectedTeamMembers", this.users)
    }
  }
}
</script>
